'use client';

import { Icon } from '@iconify/react';
import { useState } from 'react';

import type { VideoThemeCard, VideoThemesBlock } from '@/lib/reel-studio/agent/blocks';
import { formatUntranscribedBanner } from '@/lib/reel-studio/untranscribed-copy';

import { ClipGrid } from './ClipPlayer';
import { OpenInReelStudio } from './OpenInReelStudio';

function SentimentBar({ theme }: { theme: VideoThemeCard }) {
    const parts = [
        { key: 'pos', pct: theme.pos, className: 'bg-success', label: 'positive' },
        { key: 'neu', pct: theme.neu, className: 'bg-default-300', label: 'neutral' },
        { key: 'neg', pct: theme.neg, className: 'bg-danger', label: 'negative' },
    ];
    return (
        <div className="flex flex-col gap-1">
            <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-default-100">
                {parts.map((p) => (
                    <span key={p.key} className={p.className} style={{ width: `${p.pct}%` }} />
                ))}
            </div>
            <p className="text-[11px] text-default-500">
                {parts.map((p) => `${p.pct}% ${p.label}`).join(' · ')}
            </p>
        </div>
    );
}

function ThemeCard({ theme, rank }: { theme: VideoThemeCard; rank: number }) {
    const [open, setOpen] = useState(false);
    return (
        <li className="flex flex-col gap-3 rounded-lg border border-default-200 bg-content1 p-3">
            <div className="flex items-start gap-3">
                <span
                    className="mt-0.5 h-3 w-3 flex-shrink-0 rounded-full"
                    style={{ background: theme.color }}
                    aria-hidden
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <p className="text-sm font-semibold text-foreground">
                        <span className="sr-only">Theme {rank}: </span>
                        {theme.label}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">
                            {theme.pct}% of shoppers
                        </span>
                        <span className="text-xs text-default-500">
                            n={theme.base.toLocaleString()}
                        </span>
                        <span className="text-xs text-default-500">
                            · {theme.mentions.toLocaleString()} mentions
                        </span>
                        {theme.segs.map((seg) => (
                            <span
                                key={seg.label}
                                className="rounded-full bg-default-100 px-2 py-0.5 text-[11px] text-default-600"
                            >
                                {seg.label} · {seg.pct}%
                            </span>
                        ))}
                    </div>
                    <SentimentBar theme={theme} />
                </div>
            </div>
            {theme.clips.length > 0 && (
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        className="inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline"
                    >
                        <Icon icon={open ? 'lucide:chevron-up' : 'lucide:play'} width={14} />
                        {open ? 'Hide clips' : `Play ${theme.clips.length} clips`}
                    </button>
                    {open && <ClipGrid clips={theme.clips} />}
                </div>
            )}
        </li>
    );
}

/** DANI's read and the theme cards, every figure from the analysis snapshot. */
export function VideoThemesBlockView({ block }: { block: VideoThemesBlock }) {
    const untranscribed =
        block.untranscribedCount && block.untranscribedCount > 0
            ? formatUntranscribedBanner(block.untranscribedCount)
            : null;
    return (
        <section className="my-3 flex flex-col gap-3 rounded-xl border border-default-200 bg-content2/40 p-4">
            <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-foreground">
                        What people said: {block.scope.projectName}
                    </h4>
                    <p
                        className="line-clamp-2 text-xs text-default-500"
                        title={block.scope.questionText}
                    >
                        {block.scope.questionText} ·{' '}
                        {block.stats.responsesAnalyzed.toLocaleString()} responses analysed of{' '}
                        {block.stats.transcriptTotal.toLocaleString()} transcribed
                    </p>
                </div>
                <OpenInReelStudio scope={block.scope} tab="synthesis" />
            </header>
            {block.summary && (
                <div className="rounded-lg bg-content1 p-3">
                    <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-default-600">
                        <Icon icon="lucide:sparkles" width={13} /> DANI&apos;s read
                    </p>
                    <p className="text-sm leading-relaxed text-foreground">{block.summary}</p>
                </div>
            )}
            {untranscribed && <p className="text-xs text-warning-600">{untranscribed}</p>}
            <ol className="flex flex-col gap-2">
                {block.themes.map((theme, i) => (
                    <ThemeCard key={theme.id} theme={theme} rank={i + 1} />
                ))}
            </ol>
            <p className="text-[11px] text-default-400">
                Theme labels are written by DANI. Percentages are counted from the responses
                analysed, each with its base.
            </p>
        </section>
    );
}
